import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@material-ui/icons/Close';
import { useSeenHint, SEEN_HINT } from '../../../../utils/seenHints';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

/**
 * School and School Network Workspace › My Classes — the floating "?" button
 * and its seven-step guide (bug deck Ian-SNW, SW-12Oct-1, slides 56-62). The
 * same flow for both workspaces. Titles, body text and tips are the slides',
 * word for word, and the sizes are those of Ian's HTML mockup
 * ("PbDQ_SNW, SW_My Classes_Pop-up_Guidance").
 *
 * Each step shows either the page's four toolbar buttons or a class row's four
 * buttons, with the one to press ringed and the rest faded — the companion of
 * the Partner's My Schools guide (MySchoolsGuide.comp.jsx).
 */
const ORANGE = '#FF6A13';
const ORANGE_DEEP = '#F2620F';
const SEEN_KEY = 'sw_my_classes_guide_seen';

// The page toolbar, in its order; `filled` are the two orange ones.
const TOOLBAR = [
    { label: 'Export Class Details' },
    { label: 'Manage LearnCreator Teachers' },
    { label: 'Publish Learning Space', filled: true },
    { label: '+ Add a Class', filled: true },
];
// A class row's buttons, two by two.
const ROW = ['Edit Details', 'Manage Students', 'Link Programs', 'Class Dashboard'];

const STEPS = [
    {
        on: 'toolbar', hi: 3, title: 'Add a Class',
        body: 'Start by adding homeroom classes, each with its teacher, learners and parents. You’ll set up each learner’s learning space in the next steps.',
        tip: 'New classes are saved as drafts, and learners’ learning spaces aren’t activated yet.',
    },
    {
        on: 'row', hi: 0, title: 'Edit Details',
        body: 'Review the class and teacher details entered when the class was added, and make sure they’re correct.',
        tip: 'Learner and parent details are managed separately in Manage Students.',
    },
    {
        on: 'row', hi: 1, title: 'Manage Students',
        body: 'Review the learner and parent details entered when the class was added, and check each email address carefully. You can also add learners to the class here at any time.',
        tip: 'Once a class’s learning spaces are published, any learner added later has their learning space published immediately, consuming the school’s licenses.',
    },
    {
        on: 'toolbar', hi: 2, title: 'Publish Learning Space',
        body: 'Publish learning spaces once class details are confirmed, either for all classes at once or one class at a time. An activation email is then sent to each learner or their parent, depending on the parental consent method you selected.',
        tip: 'Publishing consumes the school’s licenses for each learner.',
    },
    {
        on: 'row', hi: 2, title: 'Link Programs',
        body: 'Link programs to provide assessments and learning programs to each class, including the COS Children Assessment and other programs available to you.',
        tip: 'Linked programs are available to every learner in the class whose learning space is published.',
    },
    {
        on: 'row', hi: 3, title: 'Class Dashboard',
        body: 'View each class’s dashboard to see how its learners are doing on their assessments and learning programs.',
        tip: 'The dashboard is available once more than 10 learners have completed an assessment or a learning program.',
    },
    {
        on: 'toolbar', hi: 1, title: 'Manage LearnCreator Teachers',
        body: 'Add teachers who can create programs with LearnCreator. Each teacher gets their own workspace to create programs and link them to classes.',
        tip: 'Only available when LearnCreator is linked to your workspace.',
    },
];

const LAST = STEPS.length - 1;
const RING = `0 0 0 4px var(--brand-surface, #fff), 0 0 0 7px ${ORANGE_DEEP}`;

// Sizes are the mockup's: 30px / 11px in the toolbar picture, 32px / 12px in the row picture.
const pill = (on, filled, row = false) => ({
    height: row ? 32 : 30, px: '14px', minWidth: filled ? 150 : 0, boxSizing: 'border-box', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: `1.5px solid ${ORANGE_DEEP}`, borderRadius: row ? '16px' : '15px', fontSize: row ? 12 : 11, fontWeight: 700, whiteSpace: 'nowrap',
    color: filled ? '#fff' : ORANGE_DEEP, backgroundColor: filled ? ORANGE : 'var(--brand-surface, #fff)',
    opacity: on ? 1 : 0.35, boxShadow: on ? RING : 'none',
});

const MyClassesGuide = () => {
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState(0);
    // The red dot on the "?" until this user has opened the guide once — kept
    // per user in the database (users.seen_hints), so on every device.
    const [seen, markSeen] = useSeenHint(SEEN_HINT.MY_CLASSES_GUIDE, SEEN_KEY);

    const openGuide = () => {
        setStep(0);
        setOpen(true);
        markSeen();
    };
    const closeGuide = () => setOpen(false);

    // Arrow keys move between steps (Escape is the Dialog's own).
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === 'ArrowRight') setStep((s) => Math.min(LAST, s + 1));
            if (e.key === 'ArrowLeft') setStep((s) => Math.max(0, s - 1));
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open]);

    const cur = STEPS[step];

    return (
        <>
            <Box
                component="button"
                type="button"
                onClick={openGuide}
                title="Guide to My Classes"
                aria-label="Guide to My Classes"
                sx={{
                    position: 'fixed', right: 24, bottom: 24, zIndex: 30, width: 52, height: 52,
                    borderRadius: '50%', border: 'none', backgroundColor: ORANGE, color: '#fff',
                    fontWeight: 700, fontSize: 24, cursor: 'pointer',
                    boxShadow: '0 6px 20px rgba(242,98,15,.35)',
                }}
            >
                ?
                {!seen && (
                    <Box component="span" sx={{
                        position: 'absolute', top: 2, right: 2, width: 12, height: 12, borderRadius: '50%',
                        backgroundColor: '#E5322D', border: '2px solid #FDF9F7',
                    }} />
                )}
            </Box>

            <Dialog
                open={open}
                onClose={closeGuide}
                maxWidth={false}
                // Fits a short window without a scrollbar: the spacing and the picture shrink.
                PaperProps={{ sx: { width: 900, maxWidth: '100%', borderRadius: '18px', p: { xs: '20px', sm: '28px 44px 36px' }, boxSizing: 'border-box', '@media (max-height: 820px)': { p: '18px 32px 20px' } } }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <Typography component="span" sx={{ flexShrink: 0, fontSize: 24, fontWeight: 700, color: ORANGE_DEEP }}>
                            Step {step + 1} of {STEPS.length}
                        </Typography>
                        <Typography component="h2" sx={{ m: 0, fontSize: 24, fontWeight: 700 }}>{cur.title}</Typography>
                    </Box>
                    <IconButton onClick={closeGuide} title="Close" aria-label="Close" size="small" sx={{ backgroundColor: 'var(--brand-surface-muted, #f5efeb)' }}>
                        <CloseIcon style={{ fontSize: 20, color: 'var(--brand-text-muted, #555)' }} />
                    </IconButton>
                </Box>

                {/* Where to click: the page toolbar, or one class row. */}
                <Box sx={{
                    mt: '24px', minHeight: 340, borderRadius: '12px', backgroundColor: 'var(--brand-surface-subtle, #FDF9F7)', border: '1px solid var(--brand-border, #eadfd8)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', p: '28px', boxSizing: 'border-box', overflow: 'hidden',
                    '@media (max-height: 820px)': { mt: '14px', minHeight: 220, p: '16px' },
                }}>
                    {cur.on === 'toolbar' ? (
                        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '26px' }}>
                            <Typography component="span" sx={{ fontSize: 24, fontWeight: 700, textAlign: 'center', opacity: 0.55 }}>My Classes</Typography>
                            {/* The seven stat cards, faded. */}
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: '8px', opacity: 0.35 }}>
                                {[0, 1, 2, 3, 4, 5, 6].map((k) => (
                                    <Box key={k} sx={{ height: 44, backgroundColor: 'var(--brand-surface, #fff)', border: '1px solid var(--brand-border, #f1e9e4)', borderRadius: '8px' }} />
                                ))}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px', flexWrap: 'wrap' }}>
                                {TOOLBAR.map((b, i) => (
                                    <Box key={b.label} component="span" sx={pill(i === cur.hi, b.filled)}>{b.label}</Box>
                                ))}
                            </Box>
                            {/* The class list below, faded. */}
                            <Box sx={{ height: 54, backgroundColor: 'var(--brand-surface, #fff)', border: '1px solid var(--brand-border, #f1e9e4)', borderRadius: '8px', opacity: 0.35 }} />
                        </Box>
                    ) : (
                        <Box sx={{
                            width: '100%', backgroundColor: 'var(--brand-surface, #fff)', border: '1px solid var(--brand-border, #f1e9e4)', borderRadius: '10px',
                            display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 320px' }, alignItems: 'center',
                            gap: '16px', p: '20px 22px', boxSizing: 'border-box',
                        }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', opacity: 0.55 }}>
                                <Typography component="span" sx={{ fontSize: 15, fontWeight: 500 }}>Class name</Typography>
                                <Typography component="span" sx={{ fontSize: 13, color: '#777' }}>Teacher name</Typography>
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                {ROW.map((label, i) => (
                                    <Box key={label} component="span" sx={pill(i === cur.hi, false, true)}>{label}</Box>
                                ))}
                            </Box>
                        </Box>
                    )}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', mt: '28px', minHeight: 150, '@media (max-height: 820px)': { gap: '10px', mt: '16px', minHeight: 104 } }}>
                    <Typography sx={{ m: 0, fontSize: 16, lineHeight: 1.65, color: 'var(--brand-text, #4a4542)' }}>{cur.body}</Typography>
                    <Box sx={{
                        display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: 14, color: 'var(--brand-text, #6b4a35)', lineHeight: 1.55,
                        backgroundColor: 'var(--brand-surface-muted, #FFF5EE)', borderRadius: '10px', p: '12px 16px',
                    }}>
                        <InfoOutlinedIcon style={{ fontSize: 18, color: ORANGE_DEEP, marginTop: 2 }} />
                        <span>{cur.tip}</span>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mt: '32px', pt: '24px', borderTop: '1px solid var(--brand-border, #f1e9e4)', '@media (max-height: 820px)': { mt: '14px', pt: '14px' } }}>
                    <Box sx={{ flex: 1, display: 'flex', gap: '6px' }}>
                        {STEPS.map((s, i) => (
                            <Box
                                key={s.title}
                                component="span"
                                title={s.title}
                                onClick={() => setStep(i)}
                                sx={{
                                    height: 8, width: i === step ? 22 : 8, borderRadius: '4px', cursor: 'pointer',
                                    backgroundColor: i === step ? ORANGE : '#F1DCCF',
                                }}
                            />
                        ))}
                    </Box>
                    {step > 0 && (
                        <Button
                            onClick={() => setStep((s) => Math.max(0, s - 1))}
                            sx={{
                                height: 40, px: '20px', borderRadius: '20px', border: '1.5px solid var(--brand-border, #e6dfda)', backgroundColor: 'var(--brand-surface, #fff)',
                                color: 'var(--brand-text, #444)', fontWeight: 500, fontSize: 14, textTransform: 'none',
                            }}
                        >
                            Back
                        </Button>
                    )}
                    <Button
                        onClick={() => (step === LAST ? closeGuide() : setStep((s) => s + 1))}
                        sx={{
                            height: 40, px: '24px', borderRadius: '20px', backgroundColor: ORANGE, color: '#fff',
                            fontWeight: 700, fontSize: 14, textTransform: 'none', '&:hover': { backgroundColor: ORANGE_DEEP },
                        }}
                    >
                        {step === LAST ? 'Close' : 'Next'}
                    </Button>
                </Box>
            </Dialog>
        </>
    );
};

export default MyClassesGuide;
